import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Box, CircularProgress, Grid } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { endOfDay, startOfDay } from 'date-fns'
import {
  GetUserProfileResponseDto,
  IntroducerStatusEnum,
  KycStatusEnum,
  ReadUserReferralsRequestDto,
} from '@sparling/financial-common'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { GreenButton } from '../../../../../components/Dashboard/Buttons/GreenButton'
import { InputField } from '../../../../../components/Dashboard/InputField'
import { DataPicker } from '../../../../../components/Dashboard/DatePicker'
import { KYC_STATUS } from '../../../../../constants/kycStatusList'
import { InputSelect } from '../../../../../components/Dashboard/InputSelect'
import { TableSmall } from '../../../../../components/Dashboard/TableSmall'
import { data } from './IntroducerTableLists'
import { AppStateContext, useRest } from '../../../../../components/Context'
import {
  getReferralsRequest,
  requestIntroducerCanUpdateUserDetails,
  requestIntroducerRequest,
} from '../../../../../store/actions'
import {
  getFormattedReferredUsersData,
  referredUsersIsLoading,
  requestIntroducerCanUpdateUserDetailsSelector,
} from '../../../../../store/selectors'
import { KeyValue } from '../../../../../components/Dashboard/KeyValue'
import { SingleLabel } from '../../../../../components/Dashboard/SingleLabel'
import { icons } from '../../../../../constants/icons'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  topWrap: {
    paddingTop: theme.spacing(2),
  },
  buttonPosition: {
    alignSelf: 'flex-end',
  },
  tableWrap: {
    padding: theme.spacing(0, 4, 0, 4),
    background: 'rgba(216,216,216, 0.05)',
  },
  anim: {
    color: '#6BFFBF',
    animationDuration: '550ms',
  },
  circle: {
    strokeLinecap: 'round',
  },
}))

const showStatusLabel = (status: IntroducerStatusEnum): string => {
  switch (status) {
    case IntroducerStatusEnum.REJECTED:
      return 'DECLINED'
    case IntroducerStatusEnum.PRE_APPROVED:
      return IntroducerStatusEnum.PENDING
    default:
      return status
  }
}

type Inputs = {
  startDate?: string
  endDate?: string
  status?: string
  search?: string
}

export const Introducer: FC = () => {
  const classes = useStyles()
  const { getAuthToken, getUserIdSf } = useContext(AppStateContext)
  const token = getAuthToken()
  const introducerIdSf = getUserIdSf()
  const { rxiosAuth } = useRest()
  const dispatch = useDispatch()

  const loadingReferredUsers = useSelector(referredUsersIsLoading)
  const { items, meta } = useSelector(getFormattedReferredUsersData)
  const canUpdate = useSelector(requestIntroducerCanUpdateUserDetailsSelector)

  const [userAccount, setUserAccount] = useState<null | GetUserProfileResponseDto>(null)

  const formMethods = useForm<Inputs>()

  const { watch, getValues } = formMethods

  const loadData = useCallback(async () => {
    rxiosAuth.get('user-profile').subscribe(
      // eslint-disable-next-line
      (next: any) => {
        setUserAccount(next)
      },
      (error) => {
        toast.error(error)
      },
    )
  }, [rxiosAuth])

  const loadReferrals = useCallback(
    (page: number) => {
      if (!token || !introducerIdSf) return
      dispatch(getReferralsRequest({ token, introducerIdSf, page, count: 10 }))
    },
    [dispatch, token, introducerIdSf],
  )

  const requestIntroducerHandle = useCallback(() => {
    if (token) dispatch(requestIntroducerRequest({ token }))
  }, [token, dispatch])

  const watchChange = useCallback(() => {
    if (!token || !introducerIdSf) return
    const values = getValues() as Pick<ReadUserReferralsRequestDto, 'startDate' | 'endDate' | 'status' | 'search'>
    const { startDate, endDate, status, search } = values
    dispatch(
      getReferralsRequest({
        token,
        introducerIdSf,
        startDate,
        endDate,
        status: status
          ? Object.values(KycStatusEnum).find((value) => value.replace(/\s/g, '_') === status) || status
          : undefined,
        search,
        page: 1,
        count: 10,
      }),
    )
  }, [dispatch, getValues, token, introducerIdSf])

  useEffect(() => void loadData(), []) // eslint-disable-line

  useEffect(() => {
    if (!canUpdate) return
    dispatch(requestIntroducerCanUpdateUserDetails(false))
    void loadData()
  }, [canUpdate, dispatch]) // eslint-disable-line

  useEffect(() => {
    if (!token || !introducerIdSf) return
    dispatch(getReferralsRequest({ token, introducerIdSf, page: 1, count: 10 }))
  }, [dispatch, token, introducerIdSf])

  useEffect(() => {
    const subscription = watch(watchChange)
    return () => subscription.unsubscribe()
  }, [watch, watchChange])

  const renderIntroducerStatus = useCallback((status: IntroducerStatusEnum, action: () => void) => {
    if (status === IntroducerStatusEnum.NOT_INITIAL) {
      return <GreenButton text={'Apply for Introducer'} width={160} onClick={action} />
    }
    return (
      <KeyValue labelKeyValue="Introducer Status" minWidth={270} pb={'0'}>
        <SingleLabel width={120} labelValue={showStatusLabel(status)} />
      </KeyValue>
    )
  }, [])

  return (
    <Box className={classes.container}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <FormProvider {...formMethods}>
            <Grid container direction={'row'} className={classes.topWrap}>
              <Grid item xs={8} display={'flex'} alignItems={'center'}>
                <Grid item>
                  <DataPicker
                    placeholder={'Start Date'}
                    margin={'0 8px'}
                    dataPickerName={'startDate'}
                    required={false}
                    maxDate={getValues().endDate}
                    formatValue={startOfDay}
                  />
                </Grid>
                <Grid item>
                  <DataPicker
                    placeholder={'End Date'}
                    margin={'0 8px'}
                    dataPickerName={'endDate'}
                    required={false}
                    minDate={getValues().startDate}
                    formatValue={endOfDay}
                  />
                </Grid>
                <Grid item>
                  <InputSelect
                    placeholderDisabled={false}
                    inputName={'status'}
                    placeholder="Status"
                    width={160}
                    enums={KYC_STATUS}
                    ml={8}
                    mr={8}
                  />
                </Grid>
                <Grid item>
                  <InputField
                    inputName="search"
                    placeholder="Search"
                    inputType="text"
                    marginBottom={'0'}
                    fontSize={16}
                    ml={8}
                    mr={8}
                    borderColor={'rgba(255,255,255,0.6)'}
                    prefix={icons.SEARCH}
                  />
                </Grid>
              </Grid>

              <Grid item xs={4} display={'flex'} justifyContent={'flex-end'} alignItems={'center'}>
                {userAccount && userAccount?.introducerStatus ? (
                  renderIntroducerStatus(userAccount.introducerStatus, requestIntroducerHandle)
                ) : (
                  <CircularProgress
                    disableShrink
                    className={classes.anim}
                    classes={{
                      circle: classes.circle,
                    }}
                    size={32}
                    thickness={4}
                  />
                )}
              </Grid>
            </Grid>
          </FormProvider>
        </Grid>
        <Grid item xs={12}>
          <Grid className={classes.tableWrap}>
            <TableSmall
              columns={data.columns}
              data={items}
              pb={16}
              paginationMT={16}
              params={meta}
              loading={loadingReferredUsers}
              loadData={loadReferrals}
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}
